import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface OtherDetails {
  serialNoType: string;
  serialNo: string;
}

export interface IntegrationDetails {
  farEam: string;
  systemAssetId: string;
}

export interface DocumentDetails {
  documentType: string;
  documentTags: string[];
  documentName: File | null;
}

export interface ExistingDocument {
  id: string;
  filename: string;
  file_type: string;
  [key: string]: any;
}

export interface Warranties {
  warranty_from: string | Date;
  warranty_to: string | Date;
  warrantyDocument: File | ExistingDocument | null;
  amc_vendor: string;
  warranty_vendor: string;
  insurance_start_date: Date | string;
  insurance_end_date: Date | string;
  amc_start_date: Date | string;
  amc_end_date: Date | string;
}

export interface FinancialInfo {
  depreciation_rate?: string | number;
  depreciation_start_date?: string;
  expected_date?: string;
  residual_value?: string | number;
  depreciation_status?: string;
  acc_depreciation?: string | number;
}

// Define the state interface based on the requirements
export interface AssetFormState {
  // Step 1: Asset Details
  asset_name: string;
  asset_tag: string;
  asset_category: string;
  asset_condition: string;
  parent_asset?: string;
  asset_status: string;
  serial_number?: string;
  rfid_epc_id?: string;
  image: File | null;
  images: Array<File | ExistingDocument>;

  // Step 2: Purchase Details
  manufacturer: string;
  model: string;
  purchase_value: string | number | null;
  current_value: string | number | null;
  purchase_date: string;
  vendor: string;
  invoice_date: string;
  invoice_number: number | string;

  // Step 3: Location
  location: string;
  department: string;
  assigned_to: string;
  assigned_to_email: string;

  // Step 4: Depreciation Details
  depreciation_rate: string | number,
  depreciation_start_date: string,
  expected_date: string,
  residual_value: string | number,
  depreciation_status: string,
  acc_depreciation: string | number,

  // Placeholder values for future expansion
  far_reference?: string;
  eam_reference?: string;
  // documents?: string[];
  tag_ids?: string[];

  // Step 5: Other Information
  otherInformation: OtherDetails[];

  // Step 6: Warranty Details
  warranties: Warranties[];

  // Step 7: Integration
  integration: IntegrationDetails[];

  // Step 8: Documents
  documentDetail: DocumentDetails[];

  formResetTrigger: number | null;
}

// Initial state
const initialState: AssetFormState = {
  // Step 1: Asset Details
  asset_name: '',
  asset_tag: '',
  asset_category: '',
  asset_condition: '',
  parent_asset: '',
  asset_status: '',
  serial_number: '',
  rfid_epc_id: '',
  image: null,
  images: [],

  // Step 2: Purchase Details
  manufacturer: '',
  model: '',
  purchase_value: null,
  current_value: null,
  purchase_date: '',
  vendor: '',
  invoice_date: '',
  invoice_number: '',

  // Step 3: Location
  location: '',
  department: '',
  assigned_to: '',
  assigned_to_email: '',

  // Placeholder values
  far_reference: '',
  eam_reference: '',
  // documents: [],
  tag_ids: [],

  // Step 4: Other Information
  otherInformation: [{ serialNoType: '', serialNo: '' }],

  // Step 5: Warranty Details
  warranties: [{
  warranty_from: '',
  warranty_to: '',
  warrantyDocument: null,
  amc_vendor: '',
  warranty_vendor: '',
  insurance_start_date: '',
  insurance_end_date: '',
  amc_start_date: '',
  amc_end_date: '',
}],

  // Step 5: Integration
  integration: [{ farEam: '', systemAssetId: '' }],

  // Step last: Documents
  documentDetail: [{ documentType: '', documentTags: [], documentName: null }],

  // Step : Financial Information
  depreciation_rate: '',
  depreciation_start_date: '',
  expected_date: '',
  residual_value: '',
  depreciation_status: '',
  acc_depreciation: '',
  formResetTrigger: null
};

// Create the slice
const assetCreateSlice = createSlice({
  name: 'assetCreate',
  initialState,
  reducers: {
    // Update asset details (Step 1)
    updateAssetDetails: (
      state,
      action: PayloadAction<{
        asset_name?: string;
        asset_tag?: string;
        asset_category?: string;
        location?: string;
        asset_condition?: string;
        parent_asset?: string;
        asset_status?: string;
        serial_number?: string;
        rfid_epc_id?: string;
        image?: File | null;
        images?: Array<File | ExistingDocument>;
      }>
    ) => {
      return { ...state, ...action.payload };
    },

    // Update purchase details (Step 2)
    updatePurchaseDetails: (
      state,
      action: PayloadAction<{
        manufacturer?: string;
        model?: string;
        purchase_value?: string;
        current_value?: string;
        purchase_date?: string;
        vendor?: string;
        invoice_date?: string;
        invoice_number?: number | string;
      }>
    ) => {
      return { ...state, ...action.payload };
    },

    // Update location details (Step 3)
    updateLocationDetails: (
      state,
      action: PayloadAction<{
        department?: string;
        assigned_to?: string;
      }>
    ) => {
      return { ...state, ...action.payload };
    },

    // Update Other Information (step 4)
    updateOtherInformation: (state, action: PayloadAction<OtherDetails[]>) => {
      state.otherInformation = action.payload;
    },

    // Update Warranty Details
    updateWarrantyDetails: (
      state,
      action: PayloadAction<{
        index: number;
        data: Partial<Warranties>;
      }>
    ) => {
      const { index, data } = action.payload;
      state.warranties[index] = {
        ...state.warranties[index],
        ...data,
      };
    },

    // Update Integration
    updateIntegration: (state, action: PayloadAction<IntegrationDetails[]>) => {
      state.integration = action.payload;
    },

    // Update Document Details
    updateDocumentDetails: (
      state,
      action: PayloadAction<DocumentDetails[]>
    ) => {
      state.documentDetail = action.payload;
    },

    // Financial Information
    updateFinancialInformation: (
      state,
      action: PayloadAction<Partial<FinancialInfo>>
    ) => {
      return { ...state, ...action.payload };
    },

    // Reset form state
    resetAssetForm: () => initialState,
    setAssetForm: (
      state,
      action: PayloadAction<Partial<AssetFormState>>
    ) => {
      return { ...state, ...action.payload };
    },
    setFormResetTrigger: (state) => {
      state.formResetTrigger = Date.now();
    }
  },
});

// Export actions and reducer
export const {
  updateAssetDetails,
  updatePurchaseDetails,
  updateLocationDetails,
  updateOtherInformation,
  updateWarrantyDetails,
  updateIntegration,
  updateDocumentDetails,
  updateFinancialInformation,
  resetAssetForm,
  setAssetForm,
  setFormResetTrigger,
} = assetCreateSlice.actions;

export default assetCreateSlice.reducer;
